import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BedDouble, MapPin, Home, Camera } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export type ListingsProperty = {
  id: string;
  masked_title: string | null;
  original_name: string | null;
  image_url: string | null;
  city: string | null;
  country: string | null;
  bedrooms: number | null;
  owner_website: string | null;
  is_verified: boolean | null;
  property_type: string | null;
  latitude: number | null;
  longitude: number | null;
  street_address: string | null;
  gallery_urls: string[] | null;
};

interface Props {
  property: ListingsProperty;
  index: number;
  onViewDetails: (property: ListingsProperty) => void;
}

const ListingsPropertyCard = ({ property, index, onViewDetails }: Props) => {
  const navigate = useNavigate();
  const isVerified = property.is_verified === true;
  const totalPhotos = 1 + (property.gallery_urls?.length || 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.4 }}
      className="group"
    >
      <div className="bg-card border rounded-2xl overflow-hidden shadow-sm hover:shadow-card-hover transition-all duration-300">
        {/* Image */}
        <div className="aspect-[4/3] bg-muted relative overflow-hidden cursor-pointer" onClick={() => navigate(`/listings/${property.id}`)}>
          {property.image_url ? (
            <img
              src={property.image_url}
              alt={property.masked_title || "Property"}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-secondary">
              <Home className="w-12 h-12 text-muted-foreground/30" />
            </div>
          )}
          {/* Status badge */}
          <div className="absolute top-3 left-3">
            {isVerified ? (
              <Badge className="bg-primary/90 text-primary-foreground border-0 backdrop-blur-sm text-xs">
                ✓ Verified
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-accent/90 text-accent-foreground border-0 backdrop-blur-sm text-xs">
                ⏳ Audit in Progress
              </Badge>
            )}
          </div>
          {/* Photo count badge */}
          {totalPhotos > 1 && (
            <div className="absolute bottom-3 right-3 inline-flex items-center gap-1 text-[11px] font-semibold bg-background/80 text-foreground px-2.5 py-1 rounded-full backdrop-blur-sm shadow-sm">
              <Camera className="w-3 h-3" />
              {totalPhotos} Photos
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5">
          <h3 className="font-display font-bold text-base leading-tight mb-3 truncate text-foreground">
            {property.masked_title || property.original_name || "Untitled Property"}
          </h3>

          <div className="flex flex-col gap-1.5 text-sm text-muted-foreground mb-4">
            {(property.street_address || property.city) && (
              <span className="flex items-center gap-1.5 min-w-0">
                <MapPin className="w-4 h-4 shrink-0" />
                <span className="truncate">
                  {property.street_address || `${property.city}${property.country ? `, ${property.country}` : ""}`}
                </span>
              </span>
            )}
            {(property.property_type || property.bedrooms != null) && (
              <span className="flex items-center gap-0.5">
                {property.property_type && (
                  <span className="flex items-center gap-1.5">
                    <Home className="w-4 h-4 shrink-0" />
                    {property.property_type}
                  </span>
                )}
                {property.property_type && property.bedrooms != null && (
                  <span className="text-muted-foreground/50 mx-0.5">•</span>
                )}
                {property.bedrooms != null && (
                  <span className="flex items-center gap-1.5">
                    <BedDouble className="w-4 h-4 shrink-0" />
                    {property.bedrooms} Bedroom{property.bedrooms !== 1 ? "s" : ""}
                  </span>
                )}
              </span>
            )}
          </div>

          <Button
            onClick={() => navigate(`/listings/${property.id}`)}
            className="w-full bg-gradient-hero rounded-xl shadow-savings"
          >
            View Details
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default ListingsPropertyCard;
